<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import useUser from '../../../../Composables/User/useUser.ts'
import useConfig from '../../../../Composables/useConfig'
import ResidualMode from '../../../../Constants/ResidualMode.ts'
import usePaymentTerm from '../../Composables/usePaymentTerm'
import Badge from '../UI/Badge.vue'
import FieldCreator from '../UI/Form/FieldCreator.vue'

const props = defineProps({
  contract: { type: Object, required: true },
  def: { type: Object, required: true },
  formData: { type: Object, required: true },
})

const { t, n } = useI18n()
const { isTheme } = useConfig()
const { hasRole } = useUser()
const { paymentTermLabel } = usePaymentTerm(props.contract.finance?.paymentTerm)

const periodLabel = computed(() => {
  return t(`contract.information.rentHt.${props.contract.finance?.period.id}`)
})

const setAmountInsurance = (rent) => {
  for (const [key, service] of Object.entries(props.contract.finance.services)) {
    if (service.active) {
      rent = parseFloat(rent) + parseFloat(service.amount)
    }
  }
  return n(Number(rent), 'currency')
}
</script>

<template>
  <div>
    <div v-if="contract.finance.contractMode" class="row mb-3">
      <div class="col">
        {{ def.finance.contractMode?.label ?? t('simulation.fields.finance.contractMode.label') }}
      </div>
      <div e2e-data="contractContractModeName" class="col">
        {{ contract.finance.contractMode.name }}
      </div>
    </div>

    <div class="row mb-3">
      <div class="col">
        {{ t('contract.fields.contractType.label') }}
      </div>
      <div e2e-data="contractContractTypeLabel" class="col">
        {{
          contract.finance.contractType
            ? t('contract.fields.contractType.code.' + contract.finance.contractType.code)
            : ''
        }}
      </div>
    </div>

    <div v-if="!isTheme('financia')" class="row mb-3">
      <div class="col">
        {{ t('contract.fields.leaseback.label') }}
      </div>
      <div e2e-data="contractLeaseback" class="col">
        {{ contract.leaseback ? t('main.bool.yes') : t('main.bool.no') }}
      </div>
    </div>

    <div class="row mb-3">
      <div class="col">
        {{ def.finance.duration?.label ?? t('contract.fields.duration.label') }}
      </div>
      <div e2e-data="contractDuration" class="col">{{ contract.finance.duration }} mois</div>
    </div>

    <div class="row mb-3">
      <div class="col">
        {{ def.finance.period?.label ?? t('contract.fields.period.label') }}
      </div>
      <div e2e-data="contractPeriod" class="col">
        {{
          contract.finance.period
            ? contract.finance.period.label
              ? t(contract.finance.period.label)
              : contract.finance.period.name
            : ''
        }}
      </div>
    </div>

    <div class="row mb-3">
      <div class="col">
        {{ periodLabel }}
      </div>
      <div e2e-data="contractRent" class="col">
        {{
          contract.rentInfo && contract.rentInfo.levels && contract.rentInfo.levels.length > 0
            ? t('level.hasLevel')
            : setAmountInsurance(contract.rentInfo.rent)
        }}
      </div>
    </div>

    <div class="row mb-3">
      <div class="col">
        {{ def.nbRent?.label ?? t('contract.fields.nbRent.label') }}
      </div>
      <div e2e-data="contractNbRent" class="col">
        {{ contract.nbRent }}
      </div>
    </div>

    <div v-if="contract.finance?.nbRentFree" class="row mb-3">
      <div class="col">
        {{ t('contract.fields.finance.nbRentFree.label') }}
      </div>
      <div e2e-data="contractNbRentFree" class="col">
        {{ contract.finance.nbRentFree }}
      </div>
    </div>

    <div v-if="!isTheme('financia')" class="row mb-3">
      <div class="col">
        {{ def.applicationFee?.label ?? t('contract.fields.applicationFee.label') }}
      </div>
      <div e2e-data="contractApplicationFee" class="col">
        {{ n(Number(contract.applicationFee), 'currency') }}
      </div>
    </div>

    <div v-if="contract.finance.residualMode === ResidualMode.AMOUNT" class="row mb-3">
      <div class="col">
        {{ t('contract.fields.residualValue.label') }}
      </div>
      <div e2e-data="contractResidualAmount" class="col">
        {{ n(Number(contract.finance.residualValue), 'currency') }}
      </div>
    </div>

    <div v-else-if="!contract.finance.residualAsLastRent" class="row mb-3">
      <div class="col">
        {{ t('contract.fields.residualPercent.label') }}
      </div>
      <div e2e-data="contractResidualPercent" class="col">{{ contract.finance.residualPercent }} %</div>
    </div>

    <div v-else class="row mb-3">
      <div class="col">
        {{ t('contract.fields.finance.residualAsLastRent.label') }}
      </div>
      <div e2e-data="contractResidualValue" class="col">
        {{ n(Number(contract.finance.residualValue), 'currency') }}
      </div>
    </div>

    <div class="row mb-3">
      <div class="col">
        {{ t('contract.fields.paymentTerm.label') }}
      </div>
      <div e2e-data="contractPaymentTermAndMean" class="col">
        {{ paymentTermLabel }} /
        {{
          contract.finance.paymentMean ? t('contract.fields.paymentMean.code.' + contract.finance.paymentMean.code) : ''
        }}
      </div>
    </div>

    <div class="row mb-3">
      <div class="col">
        {{ def.finance.initialRent?.label ?? t('contract.fields.finance.initialRent.label') }}
      </div>
      <div e2e-data="contractInitialRent" class="col">
        {{ n(Number(contract.finance.initialRent), 'currency') }}
      </div>
    </div>

    <div v-if="def?.orderNumber?.options?.display" class="row mb-3">
      <div class="col">
        <FieldCreator
          :form-data="formData"
          name="orderNumber"
          form-ref="formcontract"
          domain="contract"
          label-left-equal
          dynamic-label
        />
      </div>
    </div>

    <div v-if="contract.formerContractNumber" class="row mb-3">
      <div class="col">
        {{ def.formerContractNumber?.label ?? t('contract.information.formerContractNumber') }}
      </div>
      <div e2e-data="contractFormerContractNumber" class="col">
        {{ contract.formerContractNumber }}
      </div>
    </div>

    <div v-if="contract.frameworkContractNumber && !isTheme('financia')" class="row mb-3">
      <div class="col">
        {{ t('contract.information.frameworkContractNumber') }}
      </div>
      <div e2e-data="contractFrameworkContractNumber" class="col">
        {{ contract.frameworkContractNumber }}
      </div>
    </div>

    <div v-if="contract.finance.purchaseCategory" class="row mb-3">
      <div class="col">
        {{ def.finance.purchaseCategory?.label ?? t('simulation.fields.finance.purchaseCategory.label') }}
      </div>
      <div e2e-data="contractPruchaseCategoryName" class="col">
        {{ contract.finance.purchaseCategory.name }}
      </div>
    </div>

    <div v-if="contract.finance.companyCategory" class="row mb-3">
      <div class="col">
        {{ t('simulation.fields.finance.companyCategory.label') }}
      </div>
      <div e2e-data="contractCompanyCategoryName" class="col">
        {{ contract.finance.companyCategory.name }}
      </div>
    </div>

    <div v-if="hasRole('ROLE_REPORTING_VARIABLE')" class="row mb-3">
      <div class="col">
        <FieldCreator
          :form-data="formData"
          name="reportingVariables"
          form-ref="formcontract"
          domain="contract"
          select-multiple
          label-left-equal
          dynamic-label
        />
      </div>
    </div>

    <div v-if="contract.demand && isTheme('financia')" class="row mb-3">
      <div class="col">
        {{ t('demand.fields.guarantees.label') }}
      </div>
      <div class="col">
        <template v-if="contract.demand.guarantees.length > 0">
          <Badge
            v-for="guarantee in contract.demand.guarantees"
            :key="guarantee.id"
            :e2e-data="'contractDemandGuarantees-' + guarantee.id"
            type="danger"
            class="mr-1"
          >
            {{ guarantee.name }}
          </Badge>
        </template>
      </div>
    </div>
  </div>
</template>
